import cn from "classnames";
import { useTranslation } from "react-i18next";

import DownloadIcon from "assets/icons/download.svg?react";

import { Button } from "shared/ui/components/Button";
import { Loader } from "shared/ui/components/Loader";
import { Select } from "shared/ui/components/Select";
import { Typography } from "shared/ui/components/Typography";
import { ToggleButton } from "shared/ui/components/ToggleButton";

import { MoreAnalytics } from "components/MoreAnalytics";
import { AnalyticsHeader } from "components/MoreAnalytics/components/AnalyticsHeader";

import { Charts } from "./components/Charts";

import { AnalyticsProps } from "./Analytics.types";

import styles from "./Analytics.module.scss";

const Analytics = ({
  documentDownloadLoading,
  className,
  withHeader,
  showRecalculateButton,
  post,
  calculation,
  isRootAdmin,
  conclusionsPro,
  isAuthenticated,
  proCalculation,
  proCalculationCharts,
  proCalculationDocument,
  activePosition,
  analyticsType,
  energyEfficiencyClass,
  onPositionChange,
  onCalculatorStepChange,
  onAnalyticsTypeChange,
  onDownloadPDFClick,
}: AnalyticsProps) => {
  const { t } = useTranslation();

  return (
    <div className={cn(styles.analytics, className)}>
      {withHeader && (
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Typography
              className={styles.registerTitle}
              variant="bodyM"
              weight="regular"
            >
              {t("my_parameters.benefit")}
            </Typography>
            <Select
              className={styles.select}
              menuClassName={styles.selectMenu}
              controlClassName={styles.selectControl}
              dropdownIndicatorClassName={styles.selectDropdownIndicator}
              hideSelectedOptions={false}
              options={[
                { value: 10, label: `10` },
                { value: 20, label: `20` },
                { value: 30, label: `30` },
              ]}
              value={{
                value: activePosition,
                label: activePosition,
              }}
              valueRenderer={({ data }) => (
                <Typography
                  className={cn(styles.selectValue)}
                  variant="bodyM"
                  weight="regular"
                >
                  {data}
                </Typography>
              )}
              optionRenderer={({ data, innerProps, isSelected }) => {
                return (
                  <Typography
                    onClick={innerProps.onClick}
                    className={cn(styles.selectOption, {
                      [styles.selectOptionActive]: isSelected,
                    })}
                    variant="bodyS"
                    weight="semiBold"
                  >
                    {data}
                    <span />
                  </Typography>
                );
              }}
              onChange={({ value }) => onPositionChange(value)}
            />
            <Typography
              className={cn(styles.title, styles.yearsText)}
              variant="bodyM"
              weight="regular"
            >
              {t("my_parameters.parameters.years")}
            </Typography>
            <Typography
              className={styles.betaBadge}
              variant="bodyXS"
              weight="semiBold"
            >
              BETA
            </Typography>
          </div>

          <div className={styles.actions}>
            <ToggleButton
              buttonClassName={styles.toggleButtonDesktop}
              indicatorClassName={styles.toggleButtonIndicator}
              itemClassName={styles.toggleButtonItem}
              isActive={analyticsType === "details"}
              onClickToggle={onAnalyticsTypeChange}
              firstName={t("calculator_pro_page.analytics.general")}
              secondName={t("calculator_pro_page.analytics.details")}
            />
            <Button
              size="small"
              variant={"primary"}
              className={styles.actionDownload}
              startIcon={
                documentDownloadLoading ? (
                  <Loader size={20} color="#fff" />
                ) : (
                  <DownloadIcon className={styles.actionDownloadIcon} />
                )
              }
              onClick={
                onDownloadPDFClick
              }
            >
              {t("calculator_pro_page.analytics.download_documentation")}
            </Button>
            {showRecalculateButton && onCalculatorStepChange && (
              <>
                <Button
                  variant="secondary"
                  size="small"
                  className={styles.actionDownload}
                  onClick={onCalculatorStepChange}
                >
                  {t("calculator_pro_page.analytics.change_calculation")}
                </Button>
              </>
            )}
          </div>
        </div>
      )}
      {!withHeader && (
        <AnalyticsHeader
          documentDownloadLoading={documentDownloadLoading}
          isRootAdmin={isRootAdmin}
          analyticsType={analyticsType}
          onAnalyticsTypeChange={onAnalyticsTypeChange}
          activePosition={activePosition}
          onPositionChange={onPositionChange}
          onDownloadPDFClick={onDownloadPDFClick}
        />
      )}

      <ToggleButton
        buttonClassName={styles.toggleButtonMobile}
        indicatorClassName={styles.toggleButtonIndicator}
        isActive={analyticsType === "details"}
        onClickToggle={onAnalyticsTypeChange}
        firstName={t("calculator_pro_page.analytics.general")}
        secondName={t("calculator_pro_page.analytics.details")}
      />

      {calculation && analyticsType === "general" && (
        <Charts
          isAuthenticated={isAuthenticated}
          post={post}
          calculation={calculation}
          proCalculation={proCalculation}
          conclusionsPro={conclusionsPro}
          proCalculationCharts={proCalculationCharts}
          activePosition={activePosition}
          energyEfficiencyClass={energyEfficiencyClass}
          onPositionChange={onPositionChange}
        />
      )}

      {calculation && analyticsType === "details" && (
        <MoreAnalytics
          post={post}
          onPositionChange={onPositionChange}
          calculation={calculation}
          proCalculationDocument={proCalculationDocument}
          proCalculation={proCalculation}
          proCalculationCharts={proCalculationCharts}
          activePosition={activePosition}
        />
      )}
    </div>
  );
};

export default Analytics;
